/**
 * Every ISO 3166-1 alpha-2 code with an officially assigned country or territory (249). The
 * shipping config's country picker offers exactly this set, and a destination outside it never
 * reaches the database; the schema alone would accept a non-country like "UK".
 */
export const ISO_COUNTRY_CODES: readonly string[] = [
  "AD",
  "AE",
  "AF",
  "AG",
  "AI",
  "AL",
  "AM",
  "AO",
  "AQ",
  "AR",
  "AS",
  "AT",
  "AU",
  "AW",
  "AX",
  "AZ",
  "BA",
  "BB",
  "BD",
  "BE",
  "BF",
  "BG",
  "BH",
  "BI",
  "BJ",
  "BL",
  "BM",
  "BN",
  "BO",
  "BQ",
  "BR",
  "BS",
  "BT",
  "BV",
  "BW",
  "BY",
  "BZ",
  "CA",
  "CC",
  "CD",
  "CF",
  "CG",
  "CH",
  "CI",
  "CK",
  "CL",
  "CM",
  "CN",
  "CO",
  "CR",
  "CU",
  "CV",
  "CW",
  "CX",
  "CY",
  "CZ",
  "DE",
  "DJ",
  "DK",
  "DM",
  "DO",
  "DZ",
  "EC",
  "EE",
  "EG",
  "EH",
  "ER",
  "ES",
  "ET",
  "FI",
  "FJ",
  "FK",
  "FM",
  "FO",
  "FR",
  "GA",
  "GB",
  "GD",
  "GE",
  "GF",
  "GG",
  "GH",
  "GI",
  "GL",
  "GM",
  "GN",
  "GP",
  "GQ",
  "GR",
  "GS",
  "GT",
  "GU",
  "GW",
  "GY",
  "HK",
  "HM",
  "HN",
  "HR",
  "HT",
  "HU",
  "ID",
  "IE",
  "IL",
  "IM",
  "IN",
  "IO",
  "IQ",
  "IR",
  "IS",
  "IT",
  "JE",
  "JM",
  "JO",
  "JP",
  "KE",
  "KG",
  "KH",
  "KI",
  "KM",
  "KN",
  "KP",
  "KR",
  "KW",
  "KY",
  "KZ",
  "LA",
  "LB",
  "LC",
  "LI",
  "LK",
  "LR",
  "LS",
  "LT",
  "LU",
  "LV",
  "LY",
  "MA",
  "MC",
  "MD",
  "ME",
  "MF",
  "MG",
  "MH",
  "MK",
  "ML",
  "MM",
  "MN",
  "MO",
  "MP",
  "MQ",
  "MR",
  "MS",
  "MT",
  "MU",
  "MV",
  "MW",
  "MX",
  "MY",
  "MZ",
  "NA",
  "NC",
  "NE",
  "NF",
  "NG",
  "NI",
  "NL",
  "NO",
  "NP",
  "NR",
  "NU",
  "NZ",
  "OM",
  "PA",
  "PE",
  "PF",
  "PG",
  "PH",
  "PK",
  "PL",
  "PM",
  "PN",
  "PR",
  "PS",
  "PT",
  "PW",
  "PY",
  "QA",
  "RE",
  "RO",
  "RS",
  "RU",
  "RW",
  "SA",
  "SB",
  "SC",
  "SD",
  "SE",
  "SG",
  "SH",
  "SI",
  "SJ",
  "SK",
  "SL",
  "SM",
  "SN",
  "SO",
  "SR",
  "SS",
  "ST",
  "SV",
  "SX",
  "SY",
  "SZ",
  "TC",
  "TD",
  "TF",
  "TG",
  "TH",
  "TJ",
  "TK",
  "TL",
  "TM",
  "TN",
  "TO",
  "TR",
  "TT",
  "TV",
  "TW",
  "TZ",
  "UA",
  "UG",
  "UM",
  "US",
  "UY",
  "UZ",
  "VA",
  "VC",
  "VE",
  "VG",
  "VI",
  "VN",
  "VU",
  "WF",
  "WS",
  "YE",
  "YT",
  "ZA",
  "ZM",
  "ZW",
];

const ISO_COUNTRY_CODE_SET: ReadonlySet<string> = new Set(ISO_COUNTRY_CODES);

/** Whether `value` is one of the assigned alpha-2 codes (upper case, as stored). */
export function isIsoCountryCode(value: string): boolean {
  return ISO_COUNTRY_CODE_SET.has(value);
}

const displayNamesByLocale = new Map<string, Intl.DisplayNames | null>();

function displayNames(locale: string): Intl.DisplayNames | null {
  let names = displayNamesByLocale.get(locale);
  if (names === undefined) {
    try {
      names = new Intl.DisplayNames([locale], { type: "region", fallback: "code" });
    } catch {
      names = null;
    }
    displayNamesByLocale.set(locale, names);
  }
  return names;
}

/**
 * The country's name for display, from the runtime's own region names, never stored (boxed and
 * reboxed derive it the same way, so a config can never carry a stale or misspelled label). The
 * code itself when the runtime has no name for it.
 */
export function countryDisplayName(code: string, locale = "en"): string {
  const upper = code.toUpperCase();
  try {
    return displayNames(locale)?.of(upper) ?? upper;
  } catch {
    return upper;
  }
}

/**
 * boxed's frozen inventory of the country names stored on addresses from before `countryCode`
 * (`legacyAddressCountryNameToCode.ts`), so a name boxed resolves resolves here too.
 */
const LEGACY_COUNTRY_NAME_TO_CODE: Readonly<Record<string, string>> = {
  andorra: "AD",
  canada: "CA",
  germany: "DE",
  "united kingdom": "GB",
  "united states": "US",
  "united states of america": "US",
};

let codesByDisplayName: Map<string, string> | null = null;

/** Every assigned code by its English display name, case-folded, built on first use. */
function codeByDisplayName(folded: string): string | undefined {
  if (!codesByDisplayName) {
    codesByDisplayName = new Map();
    for (const code of ISO_COUNTRY_CODES) {
      const name = countryDisplayName(code);
      if (name !== code) codesByDisplayName.set(name.toLowerCase(), code);
    }
  }
  return codesByDisplayName.get(folded);
}

/**
 * A country typed or stored as text, as its ISO code: a two-letter code in any case, else one of
 * boxed's legacy names, else an English display name ("United Kingdom"), ignoring case and
 * surrounding space; boxed's Retool save resolves in the same order. Null when nothing matches.
 */
export function countryCodeFromText(text: string): string | null {
  const trimmed = text.trim();
  const upper = trimmed.toUpperCase();
  if (isIsoCountryCode(upper)) return upper;
  const folded = trimmed.toLowerCase().replace(/\s+/g, " ");
  if (!folded) return null;
  return LEGACY_COUNTRY_NAME_TO_CODE[folded] ?? codeByDisplayName(folded) ?? null;
}
